import { isConvex, quadArea, type Quad } from "./geometry";

/** Longest side, in pixels, that detection works at. Small on purpose: it
 *  runs several times a second on the live camera preview. */
export const DETECT_SIZE = 320;

/**
 * Find a photo print lying on a table, sofa or bedspread.
 *
 * Deliberately simple rather than OpenCV (an ~8 MB download): the colour
 * along the frame's border is taken to be the background, every pixel is
 * scored by how far its colour is from that, and the largest connected
 * region that stands out is the print. Its corners are the region's extreme
 * points along the two diagonals.
 *
 * This works when the print contrasts with what it lies on, which is the
 * advice the scanner shows. When it doesn't (white print on a white
 * tablecloth) it returns null and the corner handles start at a default
 * inset for the user to drag.
 */
export function detectPrint(img: ImageData): Quad | null {
  const { width: w, height: h, data } = img;
  const n = w * h;

  // Background colour: median of a band around the edge.
  const band = Math.max(2, Math.round(Math.min(w, h) * 0.04));
  const rs: number[] = [];
  const gs: number[] = [];
  const bs: number[] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (x >= band && x < w - band && y >= band && y < h - band) continue;
      const i = (y * w + x) * 4;
      rs.push(data[i]!);
      gs.push(data[i + 1]!);
      bs.push(data[i + 2]!);
    }
  }
  const med = (a: number[]) => {
    a.sort((p, q) => p - q);
    return a[a.length >> 1] ?? 0;
  };
  const br = med(rs);
  const bg = med(gs);
  const bb = med(bs);

  // Distance from the background colour, binned 0–255 for Otsu.
  const dist = new Uint8Array(n);
  const hist = new Uint32Array(256);
  for (let p = 0; p < n; p++) {
    const i = p * 4;
    const dr = data[i]! - br;
    const dg = data[i + 1]! - bg;
    const db = data[i + 2]! - bb;
    const d = Math.min(255, Math.round(Math.sqrt(dr * dr + dg * dg + db * db) * 0.75));
    dist[p] = d;
    hist[d]!++;
  }

  const { threshold: t, separation } = otsu(hist, n);
  // The two classes barely differ: the print doesn't stand out from what
  // it lies on, so there is nothing to find.
  if (separation < 30) return null;

  const mask = new Uint8Array(n);
  for (let p = 0; p < n; p++) mask[p] = dist[p]! > t ? 1 : 0;

  const region = largestComponent(mask, w, h);
  if (!region || region.size < n * 0.12) return null;

  // Extreme points along the diagonals.
  let tl = 0, tr = 0, brI = 0, bl = 0;
  let minSum = Infinity, maxSum = -Infinity, minDiff = Infinity, maxDiff = -Infinity;
  const labels = region.labels;
  for (let p = 0; p < n; p++) {
    if (labels[p] !== region.label) continue;
    const x = p % w;
    const y = (p / w) | 0;
    const s = x + y;
    const d = x - y;
    if (s < minSum) { minSum = s; tl = p; }
    if (s > maxSum) { maxSum = s; brI = p; }
    if (d > maxDiff) { maxDiff = d; tr = p; }
    if (d < minDiff) { minDiff = d; bl = p; }
  }
  const pt = (p: number) => ({ x: (p % w) / (w - 1), y: ((p / w) | 0) / (h - 1) });
  const quad: Quad = { tl: pt(tl), tr: pt(tr), br: pt(brI), bl: pt(bl) };

  const area = quadArea(quad);
  if (area < 0.12 || area > 0.995 || !isConvex(quad)) return null;
  return quad;
}

/** Otsu's threshold, plus how far apart the two classes' means are. When
 *  the classes are cleanly separated every bin in the gap scores the same,
 *  so the threshold is put in the middle of the gap rather than at its
 *  start. */
function otsu(hist: Uint32Array, total: number): { threshold: number; separation: number } {
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i]!;
  let sumB = 0;
  let wB = 0;
  let best = -1;
  let first = 0;
  let last = 0;
  let separation = 0;
  for (let i = 0; i < 256; i++) {
    wB += hist[i]!;
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += i * hist[i]!;
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > best * (1 + 1e-9)) {
      best = between;
      first = last = i;
      separation = mF - mB;
    } else if (between >= best * (1 - 1e-9)) {
      last = i;
    }
  }
  return { threshold: (first + last) >> 1, separation };
}

/** 4-connected labelling by flood fill; returns the biggest region. */
function largestComponent(
  mask: Uint8Array,
  w: number,
  h: number,
): { labels: Int32Array; label: number; size: number } | null {
  const n = w * h;
  const labels = new Int32Array(n);
  const stack = new Int32Array(n);
  let next = 0;
  let bestLabel = 0;
  let bestSize = 0;
  for (let start = 0; start < n; start++) {
    if (!mask[start] || labels[start]) continue;
    next++;
    let size = 0;
    let top = 0;
    stack[top++] = start;
    labels[start] = next;
    while (top > 0) {
      const p = stack[--top]!;
      size++;
      const x = p % w;
      if (x > 0 && mask[p - 1] && !labels[p - 1]) { labels[p - 1] = next; stack[top++] = p - 1; }
      if (x < w - 1 && mask[p + 1] && !labels[p + 1]) { labels[p + 1] = next; stack[top++] = p + 1; }
      if (p >= w && mask[p - w] && !labels[p - w]) { labels[p - w] = next; stack[top++] = p - w; }
      if (p < n - w && mask[p + w] && !labels[p + w]) { labels[p + w] = next; stack[top++] = p + w; }
    }
    if (size > bestSize) {
      bestSize = size;
      bestLabel = next;
    }
  }
  return bestLabel ? { labels, label: bestLabel, size: bestSize } : null;
}

/** Draw `source` small enough for detectPrint and read its pixels back. */
export function sampleForDetection(
  source: CanvasImageSource,
  srcW: number,
  srcH: number,
  canvas: HTMLCanvasElement,
): ImageData | null {
  const scale = DETECT_SIZE / Math.max(srcW, srcH);
  const w = Math.max(1, Math.round(srcW * scale));
  const h = Math.max(1, Math.round(srcH * scale));
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
}
